/**
 * Instanced pixel-art tower rendering.
 *
 * ## What a tower is made of
 *
 * Three stacked sprites — plate, barrel, crown — each driven by one upgrade
 * path, so the board shows what was bought. `assets/towerSprites.ts` owns the
 * drawings and the path-to-layer binding; this file bakes them and draws them.
 *
 * Four draw calls carry every tower on the map: one `InstancedMesh` per layer
 * plus one for range rings, and the ring mesh only has instances when something
 * is selected. That is one more than the two this file used to need, and it is
 * the whole budget the upgrade art was allowed. Three layers rather than four
 * is not an accident: the crossover rule caps a tower at one path above tier 2,
 * so three is enough to keep every path on its own visual axis.
 *
 * ## Why the art is baked rather than fetched
 *
 * The glyph atlas belongs to another subsystem, so this file owns its own
 * texture. `assets/towerSprites.ts` is source code, so the atlas is a
 * `DataTexture` filled at construction — no fetch, no image decode, nothing to
 * wait for before the first frame.
 *
 * Channel layout, following `render/GlyphAtlas.ts` for the same reason it does:
 *
 *   R  shade    the drawn mass, ramp stored halved
 *   G  rim      a one-pixel dilation ring, derived not authored, at one of two
 *               values: {@link RIM_LIT} where the light reaches it, else
 *               {@link RIM_PLAIN}
 *   B  hot      the `+` pixels, driven per instance by the exact tier
 *   A  cover    255 wherever any of the three is set
 *
 * Keeping shade and rim apart is what lets one drawing serve ten phosphors: the
 * shader tints R with the tower's colour and uses G to punch a dark hole, so a
 * tier-5 silhouette cuts through the lattice instead of dissolving into it.
 *
 * ## Pixel discipline
 *
 * Every art box is centred inside a 32×32 field, padded by one pixel for the
 * derived rim, and drawn on a 34×34 quad. Both are even, so a quad centred on
 * an integer position has integer edges and a `NearestFilter` atlas samples
 * texel centres exactly. Instance positions round to whole buffer pixels on the
 * CPU in `sync`, before they reach a vertex shader.
 *
 * Barrels rotate, which necessarily resamples. That is the one place sub-pixel
 * motion is wanted, because a barrel snapped to eight fixed angles reads as
 * broken rather than as retro. Crowns rotate only when they are aiming devices
 * or when their sprite asks for a spin; a coolant collar stays on the grid.
 *
 * ## HDR
 *
 * Colours come from `palette.emissive()`, so they exceed 1.0 and the bloom
 * chain carries them outward as colour. `toneMapped` is off on every material
 * here; the post stack owns tone mapping.
 */

import * as THREE from 'three';
import { VIEW } from '@/core/config';
import { emissive, INTENSITY, ROLE, type PhosphorName, type Rgb } from '@/core/palette';
import type { RenderSystem, Tower, World } from '@/core/types';
import {
  artOf, BRACKET, HOT_SHADE, SHADES, TOWER_SPRITES, TOWER_SPRITE_NAMES,
  validateTowerArt, type TowerSpriteName,
} from '@/assets/towerSprites';

// ─────────────────────────────────────────────────────────────────── atlas ──

/** Art field. Every drawing, whatever its own size, is centred in this. */
const FIELD = 32;
/** Border reserved for the derived rim. */
const PAD = 1;
/** On-screen quad edge, and the atlas cell edge. Even, so edges stay integral. */
const CELL = FIELD + PAD * 2;

const ATLAS_COLS = 15;
const ATLAS_SIZE = 512;

/**
 * The two rim values, and the light that separates them.
 *
 * One light, straight down the board, so a rim texel with the mass below it is
 * lit and one with the mass above it is in shadow. Baking the choice here
 * rather than deriving a normal in the shader costs nothing per frame and keeps
 * the fragment path to the two `step`s it already needs.
 *
 * Straight down rather than off a diagonal on purpose: it splits a silhouette
 * into a lit half and a shadowed half, and half a ring is as much of the mark
 * as this design can keep. A diagonal light lit barely a third, which measured
 * as no mark at all.
 *
 * Note the sign. Row 0 of a drawing lands at the *bottom* of the tile on
 * screen: `Renderer` flips the scene on Y and `SPRITE_VERT` flips the sampled
 * v as well, so the two cancel and the art arrives mirrored. That is a
 * pre-existing quirk and unpicking it would re-orient every tower on the board,
 * which is not this change. So the light is aimed at the art the way it is
 * actually drawn, and the lit texels are the ones whose mass sits at art -y.
 */
const RIM_LIT = 255;
const RIM_PLAIN = 96;

interface Baked {
  /** Cell index into the atlas. */
  readonly tile: number;
  /** Muzzle offset from the tile centre in buffer pixels, or 0. */
  readonly muzzle: number;
  /** Turns per second, or 0. */
  readonly spin: number;
}

const baked = new Map<TowerSpriteName, Baked>();

function buildAtlas(): THREE.DataTexture {
  validateTowerArt();

  if (TOWER_SPRITE_NAMES.length > ATLAS_COLS * Math.floor(ATLAS_SIZE / CELL)) {
    throw new Error(`TowerRenderer: ${TOWER_SPRITE_NAMES.length} sprites do not fit the atlas`);
  }

  const data = new Uint8Array(ATLAS_SIZE * ATLAS_SIZE * 4);
  const field = new Uint8Array(FIELD * FIELD);
  const hot = new Uint8Array(FIELD * FIELD);

  for (let i = 0; i < TOWER_SPRITE_NAMES.length; i++) {
    const name = TOWER_SPRITE_NAMES[i];
    const s = TOWER_SPRITES[name];
    const off = (FIELD - s.size) >> 1;

    field.fill(0);
    hot.fill(0);
    for (let y = 0; y < s.rows.length; y++) {
      const row = s.rows[y];
      for (let x = 0; x < row.length; x++) {
        const ch = row[x];
        if (ch === '.') continue;
        const o = (y + off) * FIELD + (x + off);
        if (ch === '+') { field[o] = 255; hot[o] = 255; } else { field[o] = ch.charCodeAt(0) - 48; }
      }
    }

    const ox = (i % ATLAS_COLS) * CELL;
    const oy = Math.floor(i / ATLAS_COLS) * CELL;

    for (let py = 0; py < CELL; py++) {
      for (let px = 0; px < CELL; px++) {
        const ax = px - PAD;
        const ay = py - PAD;
        const inside = ax >= 0 && ay >= 0 && ax < FIELD && ay < FIELD;
        const v = inside ? field[ay * FIELD + ax] : 0;
        const isHot = inside && hot[ay * FIELD + ax] !== 0;

        let shade = 0;
        if (isHot) shade = HOT_SHADE;
        else if (v > 0) shade = SHADES[v];

        // Eight-neighbour dilation. Four leaves a gap at every diagonal step,
        // which shows up as a dotted rim rather than an outline.
        //
        // Summing the row offsets of the occupied neighbours says which side of
        // the mass this texel sits on, which is all the light needs. See
        // RIM_LIT for why the sign reads the way it does.
        let rim = 0;
        if (shade === 0) {
          let inward = 0;
          for (let ny = -1; ny <= 1; ny++) {
            for (let nx = -1; nx <= 1; nx++) {
              if (nx === 0 && ny === 0) continue;
              const qx = ax + nx;
              const qy = ay + ny;
              if (qx < 0 || qy < 0 || qx >= FIELD || qy >= FIELD) continue;
              if (field[qy * FIELD + qx] === 0) continue;
              rim = RIM_PLAIN;
              inward -= ny;
            }
          }
          if (inward > 0) rim = RIM_LIT;
        }

        const o = ((oy + py) * ATLAS_SIZE + (ox + px)) * 4;
        // Halved so an 8-bit channel carries the over-bright top of the ramp.
        data[o] = Math.round(Math.min(1, shade * 0.5) * 255);
        data[o + 1] = rim;
        data[o + 2] = isHot ? 255 : 0;
        data[o + 3] = shade > 0 || rim > 0 ? 255 : 0;
      }
    }

    baked.set(name, {
      tile: i,
      // Art coordinates are centred in the field, so the tip sits this far from
      // the pivot however long the barrel grew.
      muzzle: s.muzzle === undefined ? 0 : s.muzzle - s.size * 0.5,
      spin: s.spin ?? 0,
    });
  }

  const tex = new THREE.DataTexture(
    data, ATLAS_SIZE, ATLAS_SIZE, THREE.RGBAFormat, THREE.UnsignedByteType,
  );
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.flipY = false;
  tex.premultiplyAlpha = false;
  tex.unpackAlignment = 4;
  tex.name = 'tower-atlas';
  tex.needsUpdate = true;
  return tex;
}

// ────────────────────────────────────────────────────────────── tier drive ──
//
// The four drawings cover six tiers, so the two tiers inside a bracket would
// otherwise be indistinguishable. These three ramps are what makes every single
// purchase show: the hot channel gets brighter, then starts to throb, then
// bleeds toward the palette's white — which out-ranks every coloured slot on
// purpose, so a tier-5 emitter reads as hotter than anything else on the board.

const HOT_DRIVE = [0.28, 0.50, 0.76, 1.02, 1.28, 1.60];
const HOT_PULSE = [0, 0, 0, 3.2, 4.8, 6.6];

/**
 * The plate is ground, not emitter. It reads at a lower rank than the gun on
 * top of it, and its hot channel has to come down with it or a tier-5 pad
 * out-blooms every barrel on the board.
 */
const PLATE_HOT = 0.42;

/**
 * Plate tint. A tier-5 pad covers thirty times the area of a barrel, and area
 * is what saturates a bloom pyramid; at the old 0.55 the biggest pads reached
 * the screen as featureless discs with their own bracing invisible inside them.
 */
const PLATE_TINT = 0.42;

/**
 * Gun tint. Under 1.0 for the same reason the plate is: a tier-5 barrel emits
 * roughly eight times the area a tier-0 one did, and at full phosphor gain
 * every structural shade on it cleared the bloom threshold, so the drawing
 * arrived as a lit outline of itself. The hot channel does the shouting.
 */
const GUN_TINT = 0.78;
const HOT_WHITE = [0, 0.03, 0.07, 0.13, 0.20, 0.30];

/**
 * Phosphor triples, resolved once each.
 *
 * `emissive()` returns a fresh tuple and `sync` asks for one per tower per
 * frame. The architecture contract bans steady-state allocation, and there are
 * only sixteen slots, so they are cached on first use.
 */
const TINT = new Map<PhosphorName, Rgb>();

/**
 * Ceiling on a tower's drive, in `core/palette.ts` intensity units.
 *
 * The Sniper wears `white`, which the palette deliberately puts *above* every
 * coloured slot at 2.6 so explosion cores out-rank enemies. A 32-pixel tier-5
 * Sniper driven at that level is not a bright tower, it is a hole in the frame:
 * measured, its scope, its rail and its mast were one saturated disc. Capping
 * here rather than editing the palette keeps the flash hierarchy intact, since
 * `uWhite` still reaches the muzzle at full strength.
 */
const TINT_CEILING = 1.95;

/**
 * Drive for the lit rim, in linear units at the peak channel.
 *
 * `BLOOM.threshold` 1.45 less `BLOOM.knee` 0.60 puts the prefilter's foot at
 * 0.85, and below that a pixel contributes *exactly* zero to the pyramid. A rim
 * is a one-pixel ring around every tower on the board; at any bloomed value it
 * would come back as a halo and turn the thing it exists to outline into the
 * blob it exists to prevent. 0.78 keeps it clear of the foot with margin, and
 * it still sits eight times the resting black floor and well above the corridor
 * fill, so it reads as a hard edge on either ground without glowing on either.
 *
 * The same argument `GRID.baseIntensity` makes, for the same reason.
 */
const RIM_DRIVE = 0.78;

/**
 * Drive for the shadowed rim, as a fraction of the layer's own fill colour.
 *
 * `render/EnemyRenderer.ts` outlines a creature at 0.06 of its fill and says
 * why: a near-black edge in the body's own hue punches a hole in the glowing
 * lattice, so the silhouette survives a busy frame. A tower wants exactly that
 * and gets it here. The layer tints — `PLATE_TINT`, `GUN_TINT` — are already
 * folded into the colour this multiplies, so a plate's outline lands darker
 * than a barrel's, which is the right way round for a thing lying on the
 * ground.
 */
const OUTLINE_DRIVE = 0.05;

function tint(name: PhosphorName): Rgb {
  let c = TINT.get(name);
  if (c === undefined) {
    c = emissive(name, Math.min(1, TINT_CEILING / INTENSITY[name]));
    TINT.set(name, c);
  }
  return c;
}

// ───────────────────────────────────────────────────────────────── shaders ──

const SPRITE_VERT = /* glsl */`
  attribute vec2 iPos;
  attribute float iTile;
  attribute vec3 iColour;
  attribute float iAngle;
  /** flash, alpha, hot drive, pulse rate. */
  attribute vec4 iFx;
  /** muzzle offset, white mix. */
  attribute vec2 iAux;

  varying vec2 vUv;
  varying vec2 vLocal;
  varying vec3 vColour;
  varying vec4 vFx;
  varying vec2 vAux;
  varying float vPhase;

  void main() {
    vec2 local = position.xy;
    float c = cos(iAngle), s = sin(iAngle);
    vec2 r = vec2(local.x * c - local.y * s, local.x * s + local.y * c);
    vLocal = local;
    vColour = iColour;
    vFx = iFx;
    vAux = iAux;
    // Towers built on different tiles must not throb in lockstep, and a hash of
    // the position costs nothing and is stable across frames.
    vPhase = dot(iPos, vec2(0.317, 0.713));

    float col = mod(iTile, ${ATLAS_COLS}.0);
    float row = floor(iTile / ${ATLAS_COLS}.0);
    vec2 cell = (vec2(col, row) * ${CELL}.0) / ${ATLAS_SIZE}.0;
    // Plane uv has v=0 at the bottom; the atlas has row 0 at the top.
    vUv = cell + vec2(uv.x, 1.0 - uv.y) * (${CELL}.0 / ${ATLAS_SIZE}.0);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(r + iPos, 0.0, 1.0);
  }
`;

const SPRITE_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D uAtlas;
  uniform vec3 uWhite;
  uniform vec3 uHotWhite;
  uniform vec3 uRim;
  uniform float uTime;
  uniform float uMuzzle;

  varying vec2 vUv;
  varying vec2 vLocal;
  varying vec3 vColour;
  varying vec4 vFx;
  varying vec2 vAux;
  varying float vPhase;

  void main() {
    vec4 t = texture2D(uAtlas, vUv);
    if (t.a < 0.5) {
      // Muzzle flash lives in the empty part of the barrel cell, so it costs no
      // extra geometry and no extra draw call. iAux.x tracks the drawn tip, so
      // a Mass Driver flashes at the end of its barrel and not inside its
      // receiver.
      float m = uMuzzle * vFx.x;
      if (m <= 0.0) discard;
      float d = length(vLocal - vec2(vAux.x, 0.0));
      float glow = m * exp(-d * 0.42);
      if (glow < 0.02) discard;
      gl_FragColor = vec4(mix(vColour, uWhite, 0.35) * glow * 2.2, glow);
      return;
    }

    // Red holds the halved shade ramp; blue flags the hot detail.
    vec3 col = vColour * (t.r * 2.0);

    float drive = vFx.z;
    if (vFx.w > 0.0) drive *= 0.70 + 0.30 * sin(uTime * vFx.w + vPhase);
    col += mix(vColour, uHotWhite, vAux.y) * t.b * drive;

    // Selection highlight, and the same channel the barrel uses to flare.
    col += vColour * vFx.x * 1.4;

    // Green is the derived rim, at one of two values. Most of it is a dark
    // outline in the tower's own hue, which is what a solid object wears and
    // what every creature already wears; the texels the light reaches carry the
    // reserved achromatic highlight that says whose the tower is. See uRim.
    float rim = step(0.25, t.g);
    float lit = step(0.75, t.g);
    col = mix(col, vColour * ${OUTLINE_DRIVE.toFixed(3)}, rim);
    col = mix(col, uRim, lit);

    gl_FragColor = vec4(col, vFx.y);
  }
`;

const RING_VERT = /* glsl */`
  attribute vec2 iPos;
  attribute float iRadius;
  attribute vec3 iColour;
  varying vec2 vLocal;
  varying vec3 vColour;
  varying float vRadius;
  void main() {
    vLocal = position.xy * iRadius;
    vColour = iColour;
    vRadius = iRadius;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(vLocal + iPos, 0.0, 1.0);
  }
`;

const RING_FRAG = /* glsl */`
  precision highp float;
  uniform float uTime;
  varying vec2 vLocal;
  varying vec3 vColour;
  varying float vRadius;

  void main() {
    float d = length(vLocal);
    // A one-pixel-wide band, so the ring stays crisp at any radius.
    float band = 1.0 - smoothstep(0.0, 1.6, abs(d - vRadius + 1.0));
    if (band <= 0.01) discard;

    // Dashes that crawl: 32 around the circumference, rotating slowly.
    float a = atan(vLocal.y, vLocal.x);
    float dash = step(0.42, fract((a / 6.28318) * 32.0 + uTime * 0.35));
    float pulse = 0.75 + 0.25 * sin(uTime * 4.0);
    float alpha = band * dash * pulse;
    if (alpha <= 0.02) discard;
    gl_FragColor = vec4(vColour * alpha * 2.4, alpha);
  }
`;

// ──────────────────────────────────────────────────────────────── the system ──

/** One slot past the tower cap, reserved for the build ghost. */
const MAX_TOWERS = 129;
const MAX_RINGS = 8;

/** How solid the ghost reads against the tile it is not standing on yet. */
const GHOST_ALPHA = 0.62;

const TAU = Math.PI * 2;

/**
 * The tower about to be built, if any.
 *
 * A mutable singleton rather than a `setGhost(...)` call taking an object,
 * because `main.ts` writes it every frame and the architecture contract bans
 * per-frame allocation. `main.ts` fills it; `sync` reads it; nothing else
 * touches it.
 */
export interface GhostState {
  active: boolean;
  archetypeId: string;
  /** Snapped tile centre, buffer pixels. */
  x: number;
  y: number;
  /** The tower's own phosphor, worn when placement is legal. */
  colour: PhosphorName;
  /** Illegal, or unaffordable. Either way it turns red. */
  blocked: boolean;
  /** Range at tier zero, drawn as the ring the player is buying. */
  range: number;
}

/**
 * Named once at module scope rather than per `flush`, because `flush` runs
 * three times a frame and the contract bans steady-state allocation.
 */
const INSTANCE_ATTRIBUTES = ['iPos', 'iTile', 'iColour', 'iAngle', 'iFx', 'iAux'];

/**
 * One instanced draw. Three of these carry every tower; the arrays are sized
 * once and never reallocated.
 */
class Layer {
  readonly geo: THREE.InstancedBufferGeometry;
  readonly mesh: THREE.Mesh;

  readonly pos = new Float32Array(MAX_TOWERS * 2);
  readonly tile = new Float32Array(MAX_TOWERS);
  readonly colour = new Float32Array(MAX_TOWERS * 3);
  readonly angle = new Float32Array(MAX_TOWERS);
  readonly fx = new Float32Array(MAX_TOWERS * 4);
  readonly aux = new Float32Array(MAX_TOWERS * 2);

  count = 0;

  constructor(quad: THREE.BufferGeometry, material: THREE.ShaderMaterial, order: number) {
    const g = new THREE.InstancedBufferGeometry();
    g.index = quad.index;
    g.setAttribute('position', quad.getAttribute('position'));
    g.setAttribute('uv', quad.getAttribute('uv'));
    g.setAttribute('iPos', new THREE.InstancedBufferAttribute(this.pos, 2));
    g.setAttribute('iTile', new THREE.InstancedBufferAttribute(this.tile, 1));
    g.setAttribute('iColour', new THREE.InstancedBufferAttribute(this.colour, 3));
    g.setAttribute('iAngle', new THREE.InstancedBufferAttribute(this.angle, 1));
    g.setAttribute('iFx', new THREE.InstancedBufferAttribute(this.fx, 4));
    g.setAttribute('iAux', new THREE.InstancedBufferAttribute(this.aux, 2));
    g.instanceCount = 0;
    this.geo = g;

    this.mesh = new THREE.Mesh(g, material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = order;
  }

  /** Writes one instance. Returns nothing; the caller owns the index. */
  write(
    i: number, px: number, py: number, tile: number, angle: number,
    r: number, g: number, b: number,
    flash: number, alpha: number, drive: number, pulse: number,
    muzzle: number, white: number,
  ): void {
    this.pos[i * 2] = px;
    this.pos[i * 2 + 1] = py;
    this.tile[i] = tile;
    this.angle[i] = angle;
    this.colour[i * 3] = r;
    this.colour[i * 3 + 1] = g;
    this.colour[i * 3 + 2] = b;
    this.fx[i * 4] = flash;
    this.fx[i * 4 + 1] = alpha;
    this.fx[i * 4 + 2] = drive;
    this.fx[i * 4 + 3] = pulse;
    this.aux[i * 2] = muzzle;
    this.aux[i * 2 + 1] = white;
  }

  flush(): void {
    this.geo.instanceCount = this.count;
    if (this.count === 0) return;
    for (let i = 0; i < INSTANCE_ATTRIBUTES.length; i++) {
      const a = this.geo.getAttribute(INSTANCE_ATTRIBUTES[i]) as THREE.InstancedBufferAttribute;
      a.needsUpdate = true;
    }
  }

  dispose(): void {
    this.geo.dispose();
  }
}

export class TowerRenderer implements RenderSystem {
  readonly name = 'towerRenderer';
  readonly group = new THREE.Group();

  readonly ghost: GhostState = {
    active: false, archetypeId: '', x: 0, y: 0, colour: 'teal', blocked: false, range: 64,
  };

  private readonly atlas: THREE.DataTexture;
  private readonly plates: Layer;
  private readonly barrels: Layer;
  private readonly crowns: Layer;
  private readonly rings: THREE.Mesh;
  private readonly ringGeo: THREE.InstancedBufferGeometry;

  private readonly ringPos = new Float32Array(MAX_RINGS * 2);
  private readonly ringRadius = new Float32Array(MAX_RINGS);
  private readonly ringColour = new Float32Array(MAX_RINGS * 3);

  private readonly ringUniforms = { uTime: { value: 0 } };
  private readonly spriteUniforms: {
    uAtlas: { value: THREE.Texture };
    uWhite: { value: THREE.Vector3 };
    uHotWhite: { value: THREE.Vector3 };
    uRim: { value: THREE.Vector3 };
    uTime: { value: number };
  };

  constructor() {
    this.atlas = buildAtlas();

    // The flash keeps the palette's full white, because a shot is an event and
    // is meant to out-rank everything. The sustained hot channel does not: at
    // full white a tier-5 emitter held that brightness every frame and its own
    // drawing disappeared inside it.
    const w = emissive(ROLE.towerFiring, 1);
    const hw = tint(ROLE.towerFiring);
    const rim = emissive(ROLE.towerRim, RIM_DRIVE / INTENSITY[ROLE.towerRim]);
    this.spriteUniforms = {
      uAtlas: { value: this.atlas },
      uWhite: { value: new THREE.Vector3(w[0], w[1], w[2]) },
      uHotWhite: { value: new THREE.Vector3(hw[0], hw[1], hw[2]) },
      uRim: { value: new THREE.Vector3(rim[0], rim[1], rim[2]) },
      uTime: { value: 0 },
    };

    const quad = new THREE.PlaneGeometry(CELL, CELL);
    // Draw order: plate, ring, barrel, crown. The ring reads as sitting on the
    // ground; the crown is the thing bolted on last, so it goes on top.
    this.plates = new Layer(quad, this.spriteMaterial(0), 10);
    this.barrels = new Layer(quad, this.spriteMaterial(1), 12);
    this.crowns = new Layer(quad, this.spriteMaterial(0), 13);

    // The ring quad is unit-sized and scaled per instance by `iRadius`.
    const ringQuad = new THREE.PlaneGeometry(2, 2);
    this.ringGeo = new THREE.InstancedBufferGeometry();
    this.ringGeo.index = ringQuad.index;
    this.ringGeo.setAttribute('position', ringQuad.getAttribute('position'));
    this.ringGeo.setAttribute('uv', ringQuad.getAttribute('uv'));
    this.ringGeo.setAttribute('iPos', new THREE.InstancedBufferAttribute(this.ringPos, 2));
    this.ringGeo.setAttribute('iRadius', new THREE.InstancedBufferAttribute(this.ringRadius, 1));
    this.ringGeo.setAttribute('iColour', new THREE.InstancedBufferAttribute(this.ringColour, 3));
    this.ringGeo.instanceCount = 0;
    this.rings = new THREE.Mesh(this.ringGeo, new THREE.ShaderMaterial({
      uniforms: this.ringUniforms,
      vertexShader: RING_VERT,
      fragmentShader: RING_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    }));
    this.rings.frustumCulled = false;
    this.rings.renderOrder = 11;

    this.group.add(this.plates.mesh, this.rings, this.barrels.mesh, this.crowns.mesh);
  }

  private spriteMaterial(muzzle: number): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        uAtlas: this.spriteUniforms.uAtlas,
        uWhite: this.spriteUniforms.uWhite,
        uHotWhite: this.spriteUniforms.uHotWhite,
        uRim: this.spriteUniforms.uRim,
        uTime: this.spriteUniforms.uTime,
        uMuzzle: { value: muzzle },
      },
      vertexShader: SPRITE_VERT,
      fragmentShader: SPRITE_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.NormalBlending,
      toneMapped: false,
    });
  }

  // ─────────────────────────────────────────────────────────────────── sync ──

  sync(world: World, alpha: number): void {
    const towers = world.towers;
    // One slot is held back so the ghost is never the instance that gets
    // dropped when the board is full.
    const n = Math.min(towers.length, MAX_TOWERS - 1);
    const time = world.time + alpha / 60;
    let rings = 0;

    this.plates.count = 0;
    this.barrels.count = 0;
    this.crowns.count = 0;

    for (let i = 0; i < n; i++) {
      const t = towers[i];
      // Towers do not move, so this is exact and the plate edges land on texel
      // boundaries every frame.
      this.writeTower(
        t.archetype.id, Math.round(t.pos.x), Math.round(t.pos.y),
        t.tiers[0], t.tiers[1], t.tiers[2],
        t.archetype.colour, t.angle, t.flash, t.selected ? 0.3 : 0, 1, time,
      );

      if (t.selected && rings < MAX_RINGS) {
        this.ringPos[rings * 2] = Math.round(t.pos.x);
        this.ringPos[rings * 2 + 1] = Math.round(t.pos.y);
        this.ringRadius[rings] = Math.max(8, Math.min(t.stats.range, MAX_RING_RADIUS));
        this.writeRingColour(rings, ROLE.towerRange, 1.0);
        rings++;
      }
    }

    // The build ghost: the same art the real tower would get at tier zero, at
    // reduced alpha, wearing red when the tile refuses it. Drawn from the tower
    // layers rather than as its own pass, so a preview costs no draw call and
    // cannot drift out of agreement with the thing it is previewing.
    const g = this.ghost;
    if (g.active && g.archetypeId !== '') {
      this.writeTower(
        g.archetypeId, Math.round(g.x), Math.round(g.y), 0, 0, 0,
        g.blocked ? ROLE.towerBlocked : g.colour, 0, 0, 0, GHOST_ALPHA, time,
      );

      // The ring answers the question the shop cannot: what does this thing
      // actually cover from here. Green or red, matching the tile under it.
      if (rings < MAX_RINGS) {
        this.ringPos[rings * 2] = Math.round(g.x);
        this.ringPos[rings * 2 + 1] = Math.round(g.y);
        this.ringRadius[rings] = Math.max(8, Math.min(g.range, MAX_RING_RADIUS));
        this.writeRingColour(rings, g.blocked ? ROLE.towerBlocked : GHOST_OK, 1.2);
        rings++;
      }
    }

    this.plates.flush();
    this.barrels.flush();
    this.crowns.flush();

    this.ringGeo.instanceCount = rings;
    if (rings > 0) {
      (this.ringGeo.getAttribute('iPos') as THREE.InstancedBufferAttribute).needsUpdate = true;
      (this.ringGeo.getAttribute('iRadius') as THREE.InstancedBufferAttribute).needsUpdate = true;
      (this.ringGeo.getAttribute('iColour') as THREE.InstancedBufferAttribute).needsUpdate = true;
    }

    // The ring animation and the hot-detail throb both want smooth time rather
    // than the sim's stepped clock, so they interpolate.
    this.ringUniforms.uTime.value = time;
    this.spriteUniforms.uTime.value = time;
  }

  /** Lays one tower's three layers down. Shared by the board and the ghost. */
  private writeTower(
    id: string, px: number, py: number,
    t0: number, t1: number, t2: number,
    colour: PhosphorName, aim: number, flash: number, select: number,
    alpha: number, time: number,
  ): void {
    const art = artOf(id);
    const tiers = TIERS;
    tiers[0] = t0; tiers[1] = t1; tiers[2] = t2;

    const c = tint(colour);
    const r = c[0];
    const gg = c[1];
    const b = c[2];

    const plateTier = tiers[art.platePath];
    const barrelTier = tiers[art.barrelPath];
    const crownTier = tiers[art.crownPath];

    // Plate. Dimmer than the gun on purpose: it is the ground, not the emitter.
    const plate = baked.get(art.plate[BRACKET[plateTier]])!;
    this.plates.write(
      this.plates.count++, px, py, plate.tile, this.spinAngle(plate, 0, time),
      r * PLATE_TINT, gg * PLATE_TINT, b * PLATE_TINT,
      select, alpha, HOT_DRIVE[plateTier] * PLATE_HOT, HOT_PULSE[plateTier], 0,
      HOT_WHITE[plateTier] * PLATE_HOT,
    );

    const barrel = baked.get(art.barrel[BRACKET[barrelTier]])!;
    this.barrels.write(
      this.barrels.count++, px, py, barrel.tile, this.spinAngle(barrel, aim, time),
      r * GUN_TINT, gg * GUN_TINT, b * GUN_TINT,
      flash, alpha, HOT_DRIVE[barrelTier], HOT_PULSE[barrelTier],
      barrel.muzzle, HOT_WHITE[barrelTier],
    );

    const crownName = art.crown[BRACKET[crownTier]];
    if (crownName !== null) {
      const crown = baked.get(crownName)!;
      this.crowns.write(
        this.crowns.count++, px, py, crown.tile,
        this.spinAngle(crown, art.crownFollowsAim ? aim : 0, time),
        r * GUN_TINT, gg * GUN_TINT, b * GUN_TINT,
        select, alpha, HOT_DRIVE[crownTier], HOT_PULSE[crownTier], 0, HOT_WHITE[crownTier],
      );
    }
  }

  /** A sprite that declares a spin ignores the aim angle and takes the clock. */
  private spinAngle(b: Baked, aim: number, time: number): number {
    return b.spin === 0 ? aim : time * b.spin * TAU;
  }

  private writeRingColour(i: number, name: PhosphorName, gain: number): void {
    const c = tint(name);
    this.ringColour[i * 3] = c[0] * gain;
    this.ringColour[i * 3 + 1] = c[1] * gain;
    this.ringColour[i * 3 + 2] = c[2] * gain;
  }

  dispose(): void {
    this.atlas.dispose();
    this.plates.dispose();
    this.barrels.dispose();
    this.crowns.dispose();
    this.ringGeo.dispose();
    (this.plates.mesh.material as THREE.Material).dispose();
    (this.barrels.mesh.material as THREE.Material).dispose();
    (this.crowns.mesh.material as THREE.Material).dispose();
    (this.rings.material as THREE.Material).dispose();
    this.group.clear();
  }
}

/**
 * Scratch tier triple. `Tower.tiers` is already one of these, but the ghost is
 * not a tower and the path indirection needs random access, so `writeTower`
 * borrows this rather than allocating per tower per frame.
 */
const TIERS = [0, 0, 0];

/** Beyond this a range circle stops being information and becomes wallpaper. */
const MAX_RING_RADIUS = Math.hypot(VIEW.width, VIEW.height) * 0.5;

/**
 * The green half of the placement answer.
 *
 * `render/PlacementOverlay.ts` paints the tile grid with the same pair, so the
 * ring and the ground under it never disagree. `uiWave` is the palette's lime;
 * there is no `towerValid` role to reach for yet.
 */
const GHOST_OK: PhosphorName = ROLE.uiWave;

/** Placement preview colour, so the build cursor agrees with the tower art. */
export function previewColour(t: Tower, blocked: boolean): PhosphorName {
  return blocked ? ROLE.towerBlocked : t.archetype.colour;
}
